import { from, of, take, map, tap, filter, catchError } from 'rxjs'
import { onError } from '@apollo/client/link/error'
import { fromPromise, FetchResult, Observable } from '@apollo/client'
import { isAuthenticationError } from '@obeta/utils/lib/isAuthenticationError'
import { AppActions } from '@obeta/models/lib/models/BusinessLayer/AppActions'
import { datadogRum } from '@datadog/browser-rum'

type Subscriber = Observable<
  FetchResult<Record<string, unknown>, Record<string, unknown>, Record<string, unknown>>
>

type Props = {
  appActions: AppActions
}

/**
 * @todo Set isRefreshing to false after second failed attempt and clear pending requests array
 *       otherwise token won't be refreshed until user refreshes page.
 *       Thing to note about onError link: if you retry op 2 time and it still fails -> 3 request won't be sent
 *       https://www.apollographql.com/docs/react/data/error-handling#retrying-operations
 */
export const getErrorLink = (props: Props) => {
  const { appActions } = props

  let isRefreshing = false
  let pendingRequests: Array<() => void> = []

  const resolvePendingRequests = () => {
    pendingRequests.map((callback) => callback())
    pendingRequests = []
  }

  const errorLink = onError((errorHandler) => {
    const { graphQLErrors, networkError, operation, forward } = errorHandler

    let forward$: Subscriber | undefined

    if (graphQLErrors) {
      graphQLErrors.forEach((error) => {
        if (isAuthenticationError(error)) {
          if (!isRefreshing) {
            isRefreshing = true
            forward$ = new Observable((observer) => {
              from(appActions.requestNewToken())
                .pipe(
                  take(1),
                  map(() => {
                    resolvePendingRequests()
                    return {}
                  }),
                  catchError(() => {
                    pendingRequests = []
                    return of(false)
                  }),
                  tap(() => {
                    isRefreshing = false
                  }),
                  filter((value) => Boolean(value))
                )
                .subscribe(observer)
              appActions.requestNewToken()
            }).flatMap(() => {
              return forward(operation)
            })
          } else {
            // Will only emit once promise is resolved
            forward$ = fromPromise(
              new Promise((resolve) => {
                pendingRequests.push(() => resolve({}))
              })
            )
          }
        } else {
          datadogRum.addError(error)
        }
      })
    } else if (networkError) {
      datadogRum.addError(networkError)
    }
    if (forward$ !== undefined) {
      forward$ = forward$.flatMap(() => {
        return forward(operation)
      })
    }

    return forward$
  })

  return errorLink
}
